import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import Link from '@/common/components/Link'
import Skeleton from '@/common/components/Skeleton'
import Table, { TableProps } from '@/common/components/Table'
import MapIcon from '@/common/components/icons/MapIcon'
import SearchIcon from '@/common/components/icons/SearchIcon'
import { PLACE_SCORE_OPTIONS } from '@/common/enums'
import useDevice from '@/common/hooks/useDevice'
import useHoveredItem from '@/common/hooks/useHoveredItem'
import useI18n from '@/common/hooks/useI18n'
import { getGoogleMapUrl } from '@/common/utils/map'
import { getTraslationObject } from '@/common/utils/object'
import { numberToString } from '@/common/utils/string'
import { resolvePath } from '@/common/utils/url'
import { isHoverableDevice } from '@/common/utils/viewport'
import { PLACE_DETAIL_PATH } from '@/modules/property/paths'

import { AdditionalScoreField } from './styled'
import { CONDO_NAME, DISTANCE, PRICE, RATING, SCORE } from './translation'

interface Data extends RentAPerfect.SearchedCondo {
  isLoading?: true
}

interface SearchPlaceTableProps extends Omit<TableProps<Data>, 'columns'> {
  isAdvanceMode: boolean
  numberOfBedroom: number
  selectedPlaceId: string | null
  setSelectedPlaceId: (placeId: string | null) => void
}

const SearchPlaceTable = ({
  data,
  isLoading,
  isAdvanceMode,
  numberOfBedroom,
  selectedPlaceId,
  setSelectedPlaceId,
  ...props
}: SearchPlaceTableProps) => {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { isMobile, isDesktop } = useDevice()
  const { hovered, getElementProps: getHoveredItemProps } = useHoveredItem()
  const [additionalScore, setAdditionalScore] = useState(
    PLACE_SCORE_OPTIONS[0].value
  )

  const getUrl = useCallback(
    (place: RentAPerfect.SearchedCondo) =>
      resolvePath(PLACE_DETAIL_PATH, { placeId: place.id }),
    []
  )

  const columns = useMemo(
    () =>
      [
        {
          id: 'name',
          Header: () => <div className='text-left'>{t(CONDO_NAME)}</div>,
          accessor: ({ isLoading, ...place }: Data) =>
            isLoading ? (
              <Skeleton width={isAdvanceMode ? (isMobile ? 80 : 100) : 150} />
            ) : (
              t(getTraslationObject(place, 'name'))
            ),
        },
        !isMobile && {
          id: 'distance',
          Header: () => (
            <div className='text-right text-nowrap'>{t(DISTANCE)}</div>
          ),
          accessor: ({ isLoading, distance }: Data) => (
            <div className='text-right'>
              {isLoading ? <Skeleton width={30} /> : numberToString(distance)}
            </div>
          ),
        },
        {
          id: 'price',
          Header: () => (
            <div className='text-right text-nowrap'>{t(PRICE)}</div>
          ),
          accessor: ({ isLoading, avgPrice }: Data) => (
            <div className='text-right'>
              {isLoading ? (
                <Skeleton width={50} />
              ) : avgPrice?.[`b${numberOfBedroom}`] ? (
                `฿${numberToString(avgPrice?.[`b${numberOfBedroom}`], {
                  round: 1000,
                })}`
              ) : (
                '-'
              )}
            </div>
          ),
        },
        {
          id: 'rating',
          Header: () => (
            <div className='text-right text-nowrap'>{t(RATING)}</div>
          ),
          accessor: ({ isLoading, rating }: Data) => (
            <div className='text-right'>
              {isLoading ? (
                <Skeleton width={30} />
              ) : rating ? (
                numberToString(rating, { decimals: 1 })
              ) : (
                '-'
              )}
            </div>
          ),
        },
        isAdvanceMode && {
          id: 'score',
          Header: () => (
            <div className='text-right text-nowrap'>{t(SCORE)}</div>
          ),
          accessor: ({ isLoading, totalScore }: Data) => (
            <div className='text-right'>
              {isLoading ? (
                <Skeleton width={30} />
              ) : (
                numberToString(totalScore, { decimals: 1 })
              )}
            </div>
          ),
        },
        isAdvanceMode &&
          !isMobile && {
            id: 'additionalScore',
            Header: () => (
              <AdditionalScoreField
                options={PLACE_SCORE_OPTIONS}
                value={additionalScore}
                onChange={setAdditionalScore}
              />
            ),
            accessor: ({ isLoading, ...place }: Data) => (
              <div className='text-right'>
                {isLoading ? (
                  <Skeleton width={30} />
                ) : place[additionalScore] ? (
                  numberToString(place[additionalScore], { decimals: 1 })
                ) : (
                  '-'
                )}
              </div>
            ),
          },
        {
          id: 'actions',
          Header: '',
          accessor: ({ isLoading, ...place }: Data) =>
            isLoading ? (
              <Skeleton width={14} />
            ) : (
              <div
                className='flex justify-center text-primary-tablet'
                onClick={event => event.stopPropagation()}
              >
                <Link
                  className='text-hover-primary flex align-center'
                  to={getGoogleMapUrl(place)}
                >
                  <MapIcon size={14} />
                </Link>
                {!(isDesktop && isHoverableDevice) && (
                  <Link
                    className='text-hover-primary flex align-center ml-12'
                    to={getUrl(place)}
                  >
                    <SearchIcon size={14} />
                  </Link>
                )}
              </div>
            ),
        },
      ].filter(Boolean),
    [
      t,
      isMobile,
      isDesktop,
      isAdvanceMode,
      additionalScore,
      numberOfBedroom,
      getUrl,
    ]
  )

  const getRowProps: TableProps<RentAPerfect.SearchedCondo>['getRowProps'] =
    useCallback(
      ({ row: { original: place } }) => {
        const { id } = place
        return {
          className: 'clickable',
          $selected:
            isDesktop && isHoverableDevice
              ? hovered === id
              : selectedPlaceId === id,
          onClick: () =>
            isDesktop && isHoverableDevice
              ? navigate(getUrl(place))
              : setSelectedPlaceId(id),
          ...(isDesktop && isHoverableDevice && getHoveredItemProps(id)),
        }
      },
      [
        isDesktop,
        hovered,
        selectedPlaceId,
        getUrl,
        getHoveredItemProps,
        setSelectedPlaceId,
        navigate,
      ]
    )

  return (
    <Table
      {...props}
      data={
        isLoading
          ? Array.from({ length: 8 }).map(() => ({ isLoading: true }))
          : data
      }
      columns={columns as TableProps['columns']}
      getRowProps={getRowProps}
    />
  )
}

export default SearchPlaceTable
